import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,

  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import moment from "moment";
import { makeStyles } from "@material-ui/core";

const customStyle = makeStyles({
  root: {
    padding: "10px 40px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "30px",
    fontFamily: "Montserrat, sans-serif",
  },
  '@media (max-width: 319px)': {
    root: { padding: "60px 5px", width: "90%", gap: "10px" },
  },
  '@media (min-width: 320px) and (max-width: 480px)': {
    root: { padding: "60px 5px", width: "90%", gap: "10px" },
  },
  '@media (min-width: 481px) and (max-width: 768px)': {
    root: { padding: "60px 20px", width: "95%", gap: "25px" },
  },
  '@media (min-width: 769px) and (max-width: 1024px)': {
    root: { padding: "70px 30px", width: "100%", gap: "30px" },
  },
});

function Layout({
  sessions,
  openModal,
  setOpenModal,
  createVisitorSession,
  deleteVisitorSession,
  updateVisitorSession,
  loading,
  loadingUser,
  loggedInUser,
  guards,
  showMessage,
}) {
  const classes = customStyle();

  // Create State
  const [guestName, setGuestName] = useState("");
  const [guests, setGuests] = useState([]);
  const [selectedGuard, setSelectedGuard] = useState("");
  const [expectedArrival, setExpectedArrival] = useState("");

  // Edit State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editGuests, setEditGuests] = useState([]);
  const [editGuestName, setEditGuestName] = useState("");
  const [editExpectedArrival, setEditExpectedArrival] = useState("");

  // Delete State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // --- Create Handlers ---
  const addGuest = () => {
    if (guestName.trim()) {
      setGuests([...guests, guestName.trim()]);
      setGuestName("");
    }
  };

  const handleCreate = () => {
    if (guests.length > 0 && selectedGuard) {
      createVisitorSession(guests, expectedArrival, selectedGuard);
      setGuests([]);
      setExpectedArrival("");
      setSelectedGuard("");
    } else if (!selectedGuard) {
      showMessage("Please select a guard.", "warning");
    }
  };

  // --- Edit Handlers ---
  const handleEditClick = (session) => {
    setEditingSession(session);
    setEditGuests([...session.guests]);
    setEditExpectedArrival(session.expectedArrival || "");
    setEditModalOpen(true);
  };

  const addEditGuest = () => {
    if (editGuestName.trim()) {
      setEditGuests([...editGuests, editGuestName.trim()]);
      setEditGuestName("");
    }
  };

  const handleUpdate = () => {
    if (editingSession && editGuests.length > 0) {
      updateVisitorSession(editingSession.id, {
        guests: editGuests,
        expectedArrival: editExpectedArrival,
      });
      setEditModalOpen(false);
      setEditingSession(null);
      setEditGuests([]);
      setEditExpectedArrival("");
    }
  };

  // --- Delete Handlers ---
  const handleDeleteClick = (sessionId) => {
    setSessionToDelete(sessionId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (sessionToDelete) {
      deleteVisitorSession(sessionToDelete);
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "CREATED":
        return "primary";
      case "CHECKED_IN":
        return "success";
      case "CHECKED_OUT":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb={3}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: "#8b003c",
            color: "#fff",
            textTransform: "none",
            borderRadius: "8px",
            fontFamily: "Montserrat, sans-serif",
            "&:hover": {
              backgroundColor: "#6d002f",
            },
          }}
          onClick={() => setOpenModal(true)}
        >
          New Visitor
        </Button>
      </Box>

      {/* Main Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          borderRadius: "20px",
          overflow: "auto",
          maxHeight: "600px",
          width: "100%",
          "&::-webkit-scrollbar": { width: "6px", height: "2px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
          "&:hover": {
            boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <Table stickyHeader>
          <TableHead sx={{ backgroundColor: "white" }}>
            <TableRow>
              {["OTP", "Guests", "Created At", "Expected Arrival", "Checked In", "Checked Out", "Status", "Action"].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    backgroundColor: "white",
                    color: "black",
                    fontFamily: "Montserrat, sans-serif",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody style={{ backgroundColor: "white" }}>
            {loadingUser ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  sx={{ py: 3, fontFamily: "Montserrat, sans-serif" }}
                >
                  <Typography color="textSecondary">
                    No visitors found.
                    {loggedInUser ? ` (User: ${loggedInUser.userId})` : " (Session Lost)"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((s) => (
                <TableRow
                  key={s.id}
                  sx={{
                    transition: "all 0.2s ease",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <TableCell
                    sx={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "bold",
                      color: "#8b003c",
                    }}
                  >
                    {s.otp}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {s.guests.map((guest, i) => (
                        <Chip
                          key={i}
                          label={guest}
                          size="small"
                          sx={{
                            backgroundColor: "#f8f9fa",
                            fontFamily: "Montserrat, sans-serif",
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    {moment(s.createdAt?.toDate ? s.createdAt.toDate() : s.createdAt).format("MMM D, h:mm A")}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    {s.expectedArrival
                      ? moment(s.expectedArrival).format("MMM D, h:mm A")
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    {s.checkedInAt
                      ? moment(s.checkedInAt?.toDate ? s.checkedInAt.toDate() : s.checkedInAt).format("MMM D, h:mm A")
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    {s.checkedOutAt
                      ? moment(s.checkedOutAt?.toDate ? s.checkedOutAt.toDate() : s.checkedOutAt).format("MMM D, h:mm A")
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    <Chip
                      label={s.status.replace("_", " ")}
                      color={getStatusColor(s.status)}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(s)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(s.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          p={4}
          bgcolor="white"
          borderRadius="16px"
          width="90%"
          maxWidth="400px"
          mx="auto"
          mt="10vh"
          boxShadow={24}
        >
          <Typography variant="h6" fontWeight="bold" mb={3} sx={{ fontFamily: "Montserrat, sans-serif" }}>
            New Visitor Session
          </Typography>

          <TextField
            label="Guest Name"
            fullWidth
            variant="outlined"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addGuest()}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Select Guard</InputLabel>
            <Select
              value={selectedGuard}
              onChange={(e) => setSelectedGuard(e.target.value)}
              label="Select Guard"
            >
              {guards.map((guard) => (
                <MenuItem key={guard.id} value={guard.id}>
                  {guard.guardName} ({guard.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            onClick={addGuest}
            disabled={!guestName.trim()}
            variant="outlined"
            size="small"
            sx={{ mb: 3 }}
          >
            Add Guest
          </Button>

          <Box mb={3} display="flex" flexWrap="wrap" gap={1}>
            {guests.map((g, i) => (
              <Chip
                key={i}
                label={g}
                onDelete={() => setGuests(guests.filter((_, idx) => idx !== i))}
              />
            ))}
          </Box>

          <TextField
            type="datetime-local"
            label="Expected Arrival (Optional)"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={expectedArrival}
            onChange={(e) => setExpectedArrival(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#8b003c",
              color: "white",
              borderRadius: "8px",
              py: 1.5,
              fontSize: "1rem",
              textTransform: "none",
              fontFamily: "Montserrat, sans-serif",
              "&:hover": {
                backgroundColor: "#6d002f",
              },
            }}
            onClick={handleCreate}
            disabled={loading || guests.length === 0}
          >
            {loading ? "Generating..." : "Generate OTP"}
          </Button>
        </Box>
      </Modal>

      {/* Edit Modal */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <Box
          p={4}
          bgcolor="white"
          borderRadius="16px"
          width="90%"
          maxWidth="400px"
          mx="auto"
          mt="10vh"
          boxShadow={24}
        >
          <Typography variant="h6" fontWeight="bold" mb={3} sx={{ fontFamily: "Montserrat, sans-serif" }}>
            Edit Visitor Session
          </Typography>

          <TextField
            label="Add Guest"
            fullWidth
            variant="outlined"
            value={editGuestName}
            onChange={(e) => setEditGuestName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addEditGuest()}
            sx={{ mb: 2 }}
          />
          <Button
            onClick={addEditGuest}
            disabled={!editGuestName.trim()}
            variant="outlined"
            size="small"
            sx={{ mb: 3 }}
          >
            Add Guest
          </Button>

          <Box mb={3} display="flex" flexWrap="wrap" gap={1}>
            {editGuests.map((g, i) => (
              <Chip
                key={i}
                label={g}
                onDelete={() => setEditGuests(editGuests.filter((_, idx) => idx !== i))}
              />
            ))}
          </Box>

          <TextField
            type="datetime-local"
            label="Expected Arrival"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={editExpectedArrival}
            onChange={(e) => setEditExpectedArrival(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              borderRadius: "8px",
              py: 1.5,
              fontSize: "1rem",
              textTransform: "none",
              fontFamily: "Montserrat, sans-serif",
            }}
            onClick={handleUpdate}
            disabled={editGuests.length === 0}
          >
            Update Session
          </Button>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: "Montserrat, sans-serif" }}>
            Are you sure you want to delete this visitor session? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Layout;
